import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import PersonnelNavBar from '../components/PersonnelNavBar';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import CheckRegisterItem from 'src/models/personnel/CheckRegisterItem';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import CheckRegisterGenerateDialog, { ICheckRegisterGenerateParams } from '../dialogs/checkRegister/CheckRegisterGenerateDialog';
import CheckRegisterWithholdingDialog from '../dialogs/checkRegister/CheckRegisterWithholdingDialog';
import CheckRegisterWorkersCompDialog from '../dialogs/checkRegister/CheckRegisterWorkersCompDialog';
import CheckRegisterReportDialog, { ICheckRegisterReportParams } from '../dialogs/checkRegister/CheckRegisterReportDialog';
import CheckDocumentManagementDialog from '../dialogs/checkRegister/CheckDocumentManagementDialog';
import VoidCheckDialog from '../dialogs/checkRegister/VoidCheckDialog';
import CheckRegisterBNoticeDialog from '../dialogs/checkRegister/CheckRegisterBNoticeDialog';
import CheckRegisterStatsDialog from '../dialogs/checkRegister/CheckRegisterStatsDialog';
import CheckRegisterExcelDialog from '../dialogs/checkRegister/CheckRegisterExcelDialog';
import CheckRegisterDownloadStubsMissingDialog from '../dialogs/checkRegister/CheckRegisterDownloadStubsMissingDialog';
import CheckRegisterPrintTSDialog from '../dialogs/checkRegister/CheckRegisterPrintTSDialog';
import NachaDialogs from '../dialogs/checkRegister/NachaDialogs';
import NoSelectedInvoicesWarningDialog from 'src/components/accounts-payable/dialogs/NoSelectedInvoicesWarningDialog';
import { FaSortAlphaDown, FaSortAlphaUp, FaBan } from 'react-icons/fa';
import CheckRegisterSearch, { ICheckRegisterSearchCriteria, CheckRegisterSelectionMode } from '../components/CheckRegisterSearch';

export const formatDateForDisplay = (value: Date | string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

export const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Mirrors the Oracle Forms Check_Register block's raw/derived status: a check is "Void" if
// VOID = 'Y', "Paid" if PRINTED = 'Y' (and not void), otherwise "Pending".
export const displayStatus = (item: CheckRegisterItem): string => {
    if (item.void === 'Y') {
        return 'Void';
    }
    if (item.printed === 'Y') {
        return 'Paid';
    }
    return 'Pending';
};

// Mirrors the Post-Query trigger's Check_Register.Doc_Status: "N/A" if check_stub has no row
// for this check yet, otherwise the date the stub was saved (docFileDate is the raw
// max(file_date) from CHECK_STUB, joined server-side the same way Status/Via use raw columns).
export const displayCheckDoc = (item: CheckRegisterItem): string => {
    if (!item.docFileDate) {
        return 'N/A';
    }
    return formatDateForDisplay(item.docFileDate);
};

// Mirrors CHECK_TYPE ('C' paper check vs 'D' direct deposit) for the "Disbursement" display column.
export const displayDisbursement = (item: CheckRegisterItem): string => {
    if (item.checkType === 'D') {
        return 'Direct Deposit';
    }
    if (item.checkType === 'C') {
        return 'Paper Check';
    }
    return item.checkType ?? '';
};

// Builds the Print button's report-download URL, or undefined if nothing is selected
// (mirroring the Oracle trigger's own no-op when no row has process = 'Y').
export const buildCheckStubReportUrl = (checkNos: number[]): string | undefined => {
    if (checkNos.length === 0) {
        return undefined;
    }
    return `/v1.0/reports/GetReport/CheckStub/checkNos/${encodeURIComponent(checkNos.join(','))}`;
};

// Builds the Print TS dialog's report-download URL. Office is optional in the Oracle dialog
// (an empty selection there means "all offices") - {office} is a required path segment here, so
// an empty/missing value is sent through as the literal string "ALL", which
// usp_CheckRegisterTimeSheetSummary already treats as "no office filter".
export const buildCheckRegisterTimeSheetSummaryUrl = (periodNo: number, office: string): string => {
    const officeSegment = office && office.trim() !== '' ? office : 'ALL';
    return `/v1.0/reports/GetReport/CheckRegisterTimeSheetSummary/periodNo/${periodNo}/office/${encodeURIComponent(officeSegment)}`;
};

// Mirrors the Check_Report block's OK button (oid 459) -> CHECKRPT.RDF, ported as
// CheckRegisterReport.rdl. All fields are optional - only non-empty ones are sent, matching
// Oracle's own "blank means no filter" semantics.
export const buildCheckRegisterReportUrl = (params: ICheckRegisterReportParams): string => {
    const query = new URLSearchParams({ paidOnly: String(!!params.paidOnly) });
    const addIfPresent = (key: string, value: string | undefined) => {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, value);
        }
    };
    addIfPresent('type', params.type);
    addIfPresent('sortBy', params.sortBy);
    addIfPresent('tsdNoList', params.tsdNoList);
    addIfPresent('checkDate', params.checkDate);
    addIfPresent('periodNo', params.beginningPeriodNo);
    addIfPresent('endPeriodNo', params.endingPeriodNo);
    addIfPresent('office', params.office);
    addIfPresent('payType', params.payType);
    addIfPresent('void', params.void);
    addIfPresent('beginningCheckDate', params.beginningCheckDate);
    addIfPresent('endingCheckDate', params.endingCheckDate);
    return `/v1.0/reports/GetReport/CheckRegisterReport?${query.toString()}`;
};

const CheckRegisterPage = () => {
    const dispatch = useDispatch();
    const { get, post, put, response } = useFetch();
    // Separate instance so this GET doesn't race/abort against the grid's own fetchCheckRegisterItems
    // request firing in the same mount tick on the shared instance above (use-http aborts an
    // in-flight request on an instance when another request starts on that same instance).
    const { get: getOffices } = useFetch();
    const { register, setValue, getValues } = useForm();
    const [checkRegisterItems, setCheckRegisterItems] = useState<Array<CheckRegisterItem>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    // Always paged at 50 rows - CHECK_REGISTER is a large, ever-growing table and an unbounded
    // fetch stalls the UI rendering the result (same issue hit on the Withholding dialog).
    const [queryOptions, setQueryOptions] = useState<ODataOptions>(new ODataOptions().addSkipTop(0, 50));
    // Only one field can be sorted at a time (ODataOptions only supports a single orderby) -
    // matches the sortField/sortDescending pattern in DepositReconciliationPage.tsx.
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(false);
    const [excelType, setExcelType] = useState<'SUMMARY' | 'DETAIL'>('SUMMARY');
    // Populated when Download Stubs finds some/all selected checks have no saved stub - drives
    // CheckRegisterDownloadStubsMissingDialog's label/canProceed. checkNos is the original
    // selection (kept stable even if grid selection changes while the dialog is open) so
    // handleProceedWithPartialStubDownload retries the exact same request with bundlePartial=true.
    const [downloadStubsMissingInfo, setDownloadStubsMissingInfo] = useState<{ checkNos: Array<number>; missingCheckNos: Array<number> } | null>(null);
    // Drives CheckRegisterPrintTSDialog's MSG status line (mirrors the Oracle dialog's own MSG
    // area, e.g. "This period does not exist. Please try again.").
    const [printTSMessage, setPrintTSMessage] = useState<string>('');
    // Drives CheckRegisterGenerateDialog's MSG line - mirrors :check_parms.msg, but only shown
    // for the blocking CheckForApproval-style alert; a successful run closes the dialog outright.
    const [generateMessage, setGenerateMessage] = useState<string>('');
    // Fetched fresh each time "Generate" is clicked (not once on mount) - mirrors the main
    // screen's own Generate trigger auto-filling :check_parms.check_no right before the dialog
    // opens. See CheckRegisterGenerateDialog's nextCheckNo prop for why the timing matters.
    const [nextCheckNo, setNextCheckNo] = useState<number | undefined>(undefined);
    // Drives CheckRegisterReportDialog's MSG-equivalent status line for the "no matching checks"
    // case, same idea as printTSMessage.
    const [reportMessage, setReportMessage] = useState<string>('');
    // Which check row's Docs window is open (double-click a row, mirrors oid 66's
    // WHEN-MOUSE-DOUBLECLICK). Undefined when the dialog is closed.
    const [docsCheck, setDocsCheck] = useState<CheckRegisterItem | undefined>(undefined);
    // Second button row (NACHA/Check Stats/Print Timesheet/B-Notice/Excel Summary/Excel Detail)
    // starts hidden behind a More/Less toggle - same idea as the search panel's own Collapse
    // button, just for the less-frequently-used half of the button set instead.
    const [showMoreButtons, setShowMoreButtons] = useState<boolean>(false);
    // Fetched once here (not per-dialog) so it can be reused both for the grid's own editable
    // Office cell and, later, as a prop into CheckRegisterSearch once its own Office filter
    // becomes a React Select.
    // Raw code+name pairs - the grid's own per-row select shows CODE (OFFICE.NAME is too long
    // for that narrow column), while CheckRegisterSearch's React Select shows NAME but filters
    // on both. Kept raw here rather than pre-collapsed to a single {value,label} shape so each
    // consumer can build its own display.
    const [officeOptions, setOfficeOptions] = useState<Array<{ code: string; dscr: string }>>([]);
    // Single-row selection for the Void action - mirrors InvoiceMaintenancePageComponent's
    // selectedInvoiceIndex/yellow-row pattern, kept deliberately separate from the existing
    // per-row selection checkboxes (those drive bulk stub actions, this drives one Void at a
    // time on whichever single row was last clicked).
    const [selectedCheckNo, setSelectedCheckNo] = useState<number | undefined>(undefined);
    // Drives the shared NoSelectedInvoicesWarningDialog (same component InvoiceMaintenancePageComponent
    // uses) for every "you must select at least one check first" case on this page.
    const [noSelectionMessage, setNoSelectionMessage] = useState<string>('');
    // Mirrors the Select All/Deselect All/Select Checks/Select Dir. Dep. buttons, but as a single
    // Fieldset of radio options - choosing one performs the selection immediately. Defaults to
    // "Deselect" since that matches the grid's initial (nothing-selected) state.
    const [selectionMode, setSelectionMode] = useState<CheckRegisterSelectionMode>('deselect');

    useEffect(() => {
        fetchCheckRegisterItems(queryOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryOptions]);

    useEffect(() => {
        const fetchOfficeOptions = async () => {
            const result = await getOffices('/v1.0/Payroll/offices');
            if (Array.isArray(result)) {
                setOfficeOptions(result);
            }
        };
        fetchOfficeOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCheckRegisterItems = async (options: ODataOptions) => {
        setLoading(true);
        const result = await get(`/v1.0/Payroll${options.toString()}`);

        if (response.ok) {
            setCheckRegisterItems(result?.items ?? []);
        } else {
            setCheckRegisterItems([]);
            console.log('Unable to get check register data!');
        }
        setLoading(false);
    };

    const showDialog = (dialog: DialogList) => {
        dispatch(actions.layout.showDialog(dialog));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    const showNoSelectionDialog = (message: string) => {
        setNoSelectionMessage(message);
        showDialog(DialogList.CHECK_REGISTER_NO_SELECTION);
    };

    // Select All / Deselect All / Select Checks / Select Dir. Dep. mirror the pattern in
    // InvoiceMaintenancePageComponent's setAllInvoicesSelectedOrDeselected - loop every row and
    // setValue its selection checkbox, rather than round-tripping to the server (the Oracle Form's
    // PROCESS column is treated as purely client-side/ephemeral selection state here).
    const setSelection = (predicate: (item: CheckRegisterItem, index: number) => boolean) => {
        checkRegisterItems.forEach((item, index) => {
            setValue(`checkRegisterItems[${index}].selection`, predicate(item, index), { shouldDirty: true });
        });
    };

    // Void checks are excluded from every "select" mode - there's no bulk action (print/save/
    // download/email stubs) that's meaningful for a voided check. Deselect All still clears
    // everything, including a voided row's checkbox if it was somehow left checked.
    const selectAll = () => setSelection((item) => item.void !== 'Y');
    const deselectAll = () => setSelection(() => false);
    const selectChecks = () => setSelection((item) => item.checkType === 'C' && item.void !== 'Y');
    const selectDirDep = () => setSelection((item) => item.checkType === 'D' && item.void !== 'Y');

    const handleSelectionModeChange = (mode: CheckRegisterSelectionMode) => {
        setSelectionMode(mode);
        switch (mode) {
            case 'select':
                selectAll();
                break;
            case 'deselect':
                deselectAll();
                break;
            case 'check':
                selectChecks();
                break;
            case 'directDeposit':
                selectDirDep();
                break;
            default:
                break;
        }
    };

    const getSelectedCheckNumbers = (): number[] => {
        const formValues = getValues();
        return checkRegisterItems
            .filter((item, index) => !!formValues?.checkRegisterItems?.[index]?.selection)
            .map((item) => item.checkNo)
            .filter((checkNo): checkNo is number => checkNo !== undefined && checkNo !== null);
    };

    const handleSort = (field: string) => {
        const descending = field === sortField ? !sortDescending : false;
        setSortField(field);
        setSortDescending(descending);

        // merge() only carries forward orderby/filters, not skip/top, so the 50-row cap has to be
        // re-applied explicitly on every rebuild of the options or it'd silently revert to the
        // server's default page size.
        const newOptions = new ODataOptions().addSkipTop(0, 50);
        newOptions.merge(queryOptions);
        newOptions.addOrderBy(field, descending ? ODataOrderByDirection.Descending : ODataOrderByDirection.Ascending);
        setQueryOptions(newOptions);
    };

    // Mirrors DepositReconciliationPage.tsx's sortable-header pattern (plain-text label + a small
    // icon previewing the direction the next click will apply, i.e. the icon always shows the
    // opposite of the sort that was just applied) - but the whole header cell is clickable, not
    // just the icon, since that's the more natural click target.
    const renderSortableHeader = (label: string, field: string, style?: React.CSSProperties) => {
        const willSortDescending = field === sortField ? !sortDescending : false;
        return (
            <th
                style={{ whiteSpace: 'nowrap', cursor: 'pointer', ...style }}
                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                onClick={() => handleSort(field)}
            >
                {label}
                <Button
                    style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                    text={''}
                    reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                    iconHoverColor="blue"
                    iconStyle={{ width: '1.25rem', height: '1.25rem', verticalAlign: 'middle' }}
                />
            </th>
        );
    };

    // Mirrors the Query menu's Enter Query/Execute Query capability on the CHECK_REGISTER block.
    // Filters use the raw CHECK_REGISTER column names (period_no/tsd_no/etc.), not the PeriodNo/
    // TsdNo aliases the SELECT list returns - the WHERE clause is evaluated before those aliases
    // exist (see the same bug fixed on the Withholding dialog's search).
    const handleSearchQuery = (criteria: ICheckRegisterSearchCriteria) => {
        const newOptions = new ODataOptions().addSkipTop(0, 50);
        if (criteria.periodNo !== undefined) {
            newOptions.addFilter(new ODataFilter('period_no', ODataFilterOperators.Equals, criteria.periodNo));
        }
        if (criteria.tsdNo !== undefined) {
            newOptions.addFilter(new ODataFilter('tsd_no', ODataFilterOperators.Equals, criteria.tsdNo));
        }
        if (criteria.office !== undefined) {
            newOptions.addFilter(new ODataFilter('office', ODataFilterOperators.Equals, criteria.office));
        }
        if (criteria.paidTo !== undefined) {
            newOptions.addFilter(new ODataFilter('paid_to', ODataFilterOperators.Contains, criteria.paidTo));
        }
        if (criteria.checkNo !== undefined) {
            newOptions.addFilter(new ODataFilter('check_no', ODataFilterOperators.Contains, criteria.checkNo));
        }
        if (criteria.checkDate !== undefined) {
            newOptions.addFilter(new ODataFilter('check_date', ODataFilterOperators.Equals, formatDateForDisplay(criteria.checkDate)));
        }
        if (criteria.checkType !== undefined) {
            newOptions.addFilter(new ODataFilter('check_type', ODataFilterOperators.Equals, criteria.checkType));
        }
        if (criteria.amount !== undefined) {
            newOptions.addFilter(new ODataFilter('amount', criteria.amountLike ? ODataFilterOperators.Contains : ODataFilterOperators.Equals, criteria.amount));
        }
        // Status mirrors displayStatus() - Void takes precedence over Printed.
        if (criteria.status === 'Void') {
            newOptions.addFilter(new ODataFilter('void', ODataFilterOperators.Equals, 'Y'));
        } else if (criteria.status === 'Paid') {
            newOptions.addFilter(new ODataFilter('printed', ODataFilterOperators.Equals, 'Y'));
            newOptions.addFilter(new ODataFilter('void', ODataFilterOperators.NotEquals, 'Y'));
        } else if (criteria.status === 'Pending') {
            newOptions.addFilter(new ODataFilter('printed', ODataFilterOperators.NotEquals, 'Y'));
            newOptions.addFilter(new ODataFilter('void', ODataFilterOperators.NotEquals, 'Y'));
        }
        if (criteria.stubStatus === 'Yes') {
            newOptions.addFilter(new ODataFilter('has_stub_doc', ODataFilterOperators.Equals, 'Y'));
        } else if (criteria.stubStatus === 'No') {
            newOptions.addFilter(new ODataFilter('has_stub_doc', ODataFilterOperators.Equals, 'N'));
        }
        setQueryOptions(newOptions);
    };

    const showGenerate = async () => {
        setGenerateMessage('');
        setNextCheckNo(undefined);
        showDialog(DialogList.CHECK_REGISTER_GENERATE);

        const result = await get('/v1.0/Payroll/nextcheckno');
        if (typeof result === 'number') {
            setNextCheckNo(result);
        }
    };
    const showWithholding = () => showDialog(DialogList.CHECK_REGISTER_WITHHOLDING);
    const showWorkersComp = () => showDialog(DialogList.CHECK_REGISTER_WORKERS_COMP);
    const handleOpenDocuments = (item: CheckRegisterItem) => {
        setDocsCheck(item);
        showDialog(DialogList.CHECK_REGISTER_DOCUMENTS);
    };

    const handleVoidButtonClicked = (item: CheckRegisterItem) => {
        setSelectedCheckNo(item.checkNo);
        showDialog(DialogList.CHECK_REGISTER_VOID);
    };

    // Mirrors the grid's own editable VOID field + PRE-UPDATE trigger (oid 30), fired here by
    // the Void dialog's confirm instead of an in-place field edit + form save (no generic Forms
    // "Save" action exists on the web to hook the same way). One-way - un-voiding was dropped, see
    // VoidCheckDialog.
    const handleConfirmVoid = async (voidReason: string) => {
        if (selectedCheckNo === undefined) {
            return;
        }
        await put(`/v1.0/Payroll/${selectedCheckNo}/void`, { voidReason });
        if (response.ok) {
            hideDialog();
            fetchCheckRegisterItems(queryOptions);
        } else {
            alert('Unable to update the void status. Please try again.');
        }
    };

    const showReport = () => {
        setReportMessage('');
        showDialog(DialogList.CHECK_REGISTER_REPORT);
    };
    const showBNotice = () => showDialog(DialogList.CHECK_REGISTER_BNOTICE);
    const showStats = () => showDialog(DialogList.CHECK_REGISTER_STATS);
    const showExcelSummary = () => {
        setExcelType('SUMMARY');
        showDialog(DialogList.CHECK_EXCEL_REPORT);
    };
    const showExcelDetail = () => {
        setExcelType('DETAIL');
        showDialog(DialogList.CHECK_EXCEL_REPORT);
    };

    // Mirrors the Print button's Oracle trigger: no-op if nothing is selected, otherwise
    // print exactly the selected checks (the trigger's own "where process = 'Y'" filter,
    // ported here as an explicit check-number list since selection is client-side only).
    const handlePrint = async () => {
        const checkNos = getSelectedCheckNumbers();
        const url = buildCheckStubReportUrl(checkNos);
        if (!url) {
            showNoSelectionDialog('Please select at least one check.');
            return;
        }

        await get(url);
        if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = 'CheckStub.pdf';
            a.click();
            window.URL.revokeObjectURL(blobUrl);
        } else {
            console.log('Unable to get check stub report!');
        }
    };

    // Mirrors the Save Stubs button's Oracle trigger: per selected check, Create_Check_Pdf +
    // mark printed='Y' (ported here as one server round trip that generates the stub PDF via
    // the same SSRS report Print uses, uploads it to S3, and records it in CHECK_STUB). Refetch
    // the grid afterward so the Status column picks up the new Paid/printed state.
    const handleSaveStubs = async () => {
        const checkNos = getSelectedCheckNumbers();
        if (checkNos.length === 0) {
            showNoSelectionDialog('Please select at least one check.');
            return;
        }

        await post('/v1.0/Payroll/savestubs', checkNos);
        if (response.ok) {
            const { saved } = await response.json();
            if (saved.length < checkNos.length) {
                alert(`Saved ${saved.length} of ${checkNos.length} stub(s). Some checks had no report data available.`);
            }
            fetchCheckRegisterItems(queryOptions);
        } else {
            console.log('Unable to save check stubs!');
        }
    };

    // Mirrors the Download Stubs button's Oracle trigger: unlike Save Stubs, Create_CheckStub_Files
    // does NOT generate anything fresh - it only extracts whatever was already saved to check_stub
    // by a prior Save Stubs run and merges those into one Check-Stubs.pdf (the save-file dialog
    // itself is dropped per the confirmed web-port rule; this downloads straight to the browser's
    // default location). If some selected checks have no saved stub, the server returns the list
    // instead of a file so the user can choose to bundle what's available or abort, rather than
    // silently producing a partial PDF the way Oracle's own unguarded merge would. If NONE of the
    // selected checks have a saved stub, there's no "rest" to bundle - canProceed is false and the
    // dialog only offers Close, instead of a nonsensical "proceed anyway" with nothing to proceed with.
    const requestDownloadStubs = async (checkNos: Array<number>, bundlePartial: boolean) => {
        await post(`/v1.0/Payroll/downloadstubs?bundlePartial=${bundlePartial}`, checkNos);
        if (!response.ok) {
            console.log('Unable to download check stubs!');
            return;
        }

        if (response.status === 204) {
            setDownloadStubsMissingInfo({ checkNos, missingCheckNos: checkNos });
            showDialog(DialogList.CHECK_REGISTER_DOWNLOAD_STUBS_MISSING);
            return;
        }

        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
            const { missingCheckNos } = await response.json();
            setDownloadStubsMissingInfo({ checkNos, missingCheckNos });
            showDialog(DialogList.CHECK_REGISTER_DOWNLOAD_STUBS_MISSING);
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'Check-Stubs.pdf';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
    };

    const handleDownloadStubs = async () => {
        const checkNos = getSelectedCheckNumbers();
        if (checkNos.length === 0) {
            showNoSelectionDialog('Please select at least one check.');
            return;
        }

        await requestDownloadStubs(checkNos, false);
    };

    // The dialog's "Proceed anyway?" button - re-requests the same original selection with
    // bundlePartial=true so the server merges whatever's available and skips the rest.
    const handleProceedWithPartialStubDownload = async () => {
        if (!downloadStubsMissingInfo) {
            return;
        }
        hideDialog();
        await requestDownloadStubs(downloadStubsMissingInfo.checkNos, true);
    };

    // Mirrors the Print TS button's Oracle trigger: Go_Block('TE_Standard') - just opens the
    // params dialog. The OK button's actual report call is wired separately.
    const handlePrintTS = () => {
        setPrintTSMessage('');
        showDialog(DialogList.CHECK_REGISTER_PRINT_TS);
    };

    // Mirrors the TE_Standard params dialog's OK button: runs expbypr.rdf via the report
    // server and downloads the resulting PDF, defaulting to the same "TimeSheets.pdf" filename
    // Oracle's own file-save dialog defaulted to. If the period/office combination has no
    // matching non-void checks the endpoint returns 204 (mirrors Oracle's own report simply
    // printing nothing for an unmatched period) - surfaced via the dialog's message line rather
    // than a silent no-op, since a downloaded-but-empty PDF would be more confusing than clearer.
    const handlePrintTSSubmit = async (periodNo: number, office: string) => {
        setPrintTSMessage('');

        if (!periodNo) {
            setPrintTSMessage('Please enter a period number.');
            return;
        }

        const url = buildCheckRegisterTimeSheetSummaryUrl(periodNo, office);
        await get(url);

        if (!response.ok) {
            setPrintTSMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (response.status === 204) {
            setPrintTSMessage('This period does not exist. Please try again.');
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'TimeSheets.pdf';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Mirrors the Check_Parms dialog's OK button -> Generate_Checks. usp_GenerateChecks raises
    // a blocking error (mirrors CheckForApproval's Alert_Message) as a 400 with the same message
    // text - surfaced in the dialog rather than closing it, same as Print TS's own message line.
    const handleGenerateSubmit = async (params: ICheckRegisterGenerateParams) => {
        setGenerateMessage('');

        if (!params.periodNo || !params.beginningCheckNumber || !params.checkDate) {
            setGenerateMessage('Pay period, beginning check number, and check date are required.');
            return;
        }

        await post('/v1.0/Payroll/generate', {
            periodNo: params.periodNo,
            beginningCheckNo: params.beginningCheckNumber,
            checkDate: params.checkDate,
            type: params.type,
            tsdNoList: params.tsdNoList,
            office: params.office,
            overrideDirectDeposit: params.overrideDirectDeposit,
        });

        if (!response.ok) {
            const body = await response.text();
            setGenerateMessage(body || 'Unable to generate checks. Please try again.');
            return;
        }

        const result: { checksCreated: number } = await response.json();
        hideDialog();
        fetchCheckRegisterItems(queryOptions);
        alert(`Generated ${result.checksCreated} check(s).`);
    };

    // Mirrors the Check_Report dialog's OK button (oid 459) -> CHECKRPT.RDF, ported as
    // CheckRegisterReport.rdl on the report server. Same 204-means-nothing-matched handling as
    // Print Timesheet's own report call.
    const handleGenerateReport = async (params: ICheckRegisterReportParams) => {
        setReportMessage('');

        const url = buildCheckRegisterReportUrl(params);
        await get(url);

        if (!response.ok) {
            setReportMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (response.status === 204) {
            setReportMessage('No checks matched those filters.');
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'CheckReport.pdf';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Mirrors the E-Mail Stubs button's Oracle trigger: per selected check, Submit_Job('send_check_stub(...)')
    // - only fires when a stub was already saved (same precondition Download Stubs checks).
    // Oracle's version is a fire-and-forget background job with no user feedback at all; this
    // reports back what actually happened, which is a deliberate improvement, not a deviation.
    const handleEmailStubs = async () => {
        const checkNos = getSelectedCheckNumbers();
        if (checkNos.length === 0) {
            showNoSelectionDialog('Please select at least one check.');
            return;
        }

        await post('/v1.0/Payroll/emailstubs', checkNos);
        if (!response.ok) {
            console.log('Unable to email check stubs!');
            return;
        }

        const { sent, missingStub, missingEmail } = await response.json();
        if (missingStub.length > 0 || missingEmail.length > 0) {
            const parts = [
                sent.length > 0 ? `Emailed ${sent.length} of ${checkNos.length} stub(s).` : 'No stubs were emailed.',
            ];
            if (missingStub.length > 0) {
                parts.push(`No saved stub yet (run Save Stubs first): ${missingStub.join(', ')}.`);
            }
            if (missingEmail.length > 0) {
                parts.push(`No email on file for: ${missingEmail.join(', ')}.`);
            }
            alert(parts.join(' '));
        }
    };

    // Mirrors the NACHA Direct Deposit button [oid 536] -> Go_Block('DD_Parms'): the params
    // dialog is the real entry point, not the batch list (both its OK and Cancel buttons lead
    // to Nacha_Control/"Deposit" afterward - see NachaDialogs.tsx).
    const handleDirectDeposit = () => showDialog(DialogList.NACHA_GENERATE);

    // No fixed width - .dlc-form-button-default only sets padding, so each button sizes to its
    // own text content (previously a fixed '8%' width clipped longer labels like "Worker's Comp").
    const buttonWidth = { whiteSpace: 'nowrap' as const, height: '3rem', marginLeft: '.5rem' };

    const downloadStubsMissingAllSelected = !!downloadStubsMissingInfo
        && downloadStubsMissingInfo.missingCheckNos.length === downloadStubsMissingInfo.checkNos.length;
    const downloadStubsMissingLabel = downloadStubsMissingAllSelected
        ? 'None of the selected checks have a saved stub yet. Run Save Stubs first.'
        : `${downloadStubsMissingInfo?.missingCheckNos.length ?? 0} of ${downloadStubsMissingInfo?.checkNos.length ?? 0} selected check(s) ` +
            `have no saved stub yet (run Save Stubs first): ${downloadStubsMissingInfo?.missingCheckNos.join(', ')}.`;

    return (
        <div>
            <PersonnelNavBar />
            <CheckRegisterSearch
                runQuery={handleSearchQuery}
                checkRegisterItems={checkRegisterItems}
                selectionMode={selectionMode}
                onSelectionModeChange={handleSelectionModeChange}
                officeOptions={officeOptions}
            />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ marginBottom: '.5rem', alignItems: 'center', justifyContent: 'center' }}>
                                <Button className="blue-hover-button" style={buttonWidth} text="Generate" onClick={showGenerate} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Print Stubs" onClick={handlePrint} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Save Stubs" onClick={handleSaveStubs} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Download Stubs" onClick={handleDownloadStubs} />
                                <Button className="blue-hover-button" style={buttonWidth} text="E-Mail Stubs" onClick={handleEmailStubs} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Withholding" onClick={showWithholding} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Report" onClick={showReport} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Check Stats" onClick={showStats} />
                                <Button className="blue-hover-button" style={buttonWidth} text="B-Notice Withholding" onClick={showBNotice} />
                                <Button className="blue-hover-button" style={buttonWidth} text="NACHA Direct Deposit" onClick={handleDirectDeposit} />
                                <Button buttonStyle="link" style={{ textDecoration: 'underline' }} text={showMoreButtons ? 'Less' : 'More'} onClick={() => setShowMoreButtons(!showMoreButtons)} />
                            </div>
                            {showMoreButtons && (
                                <div className="form-row" style={{ marginBottom: '.5rem', justifyContent: 'center' }}>
                                    <Button className="blue-hover-button" style={buttonWidth} text="Worker's Comp" onClick={showWorkersComp} />
                                    <Button className="blue-hover-button" style={buttonWidth} text="Print Timesheet" onClick={handlePrintTS} />
                                    <Button className="blue-hover-button" style={buttonWidth} text="Excel Summary" onClick={showExcelSummary} />
                                    <Button className="blue-hover-button" style={buttonWidth} text="Excel Detail" onClick={showExcelDetail} />
                                </div>
                            )}
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="Check Register">
                            <div style={{ overflowY: 'scroll', maxHeight: '32rem' }}>
                                <table style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '1%', padding: 0 }} />
                                            {renderSortableHeader('Period #', 'PeriodNo', { width: '8%' })}
                                            {renderSortableHeader('DLC #', 'TsdNo', { width: '8%' })}
                                            <th style={{ width: '6%' }}>Office</th>
                                            {renderSortableHeader('Paid To', 'PaidTo', { width: '20%' })}
                                            {renderSortableHeader('Check #', 'CheckNo', { width: '9%' })}
                                            {renderSortableHeader('Check Date', 'CheckDate', { width: '9%' })}
                                            <th style={{ width: '4%' }}>Type</th>
                                            {renderSortableHeader('Amount', 'Amount', { width: '10%' })}
                                            <th style={{ width: '8%' }}>Status</th>
                                            <th style={{ width: '4%' }}>Void</th>
                                            <th style={{ width: '10%' }}>Disbursement</th>
                                            <th style={{ width: '6%', whiteSpace: 'nowrap' }}>Saved Stub</th>
                                            <th style={{ width: '3%' }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr>
                                                <td colSpan={14} className="bold-centered">
                                                    <LoadingSpinner />
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && checkRegisterItems.map((item, index) => (
                                            <tr
                                                key={`${item.checkNo}-${index}`}
                                                className="table-item-hover"
                                                style={{
                                                    textAlign: 'center',
                                                    cursor: item.void === 'Y' ? 'default' : 'pointer',
                                                    opacity: item.void === 'Y' ? 0.5 : 1,
                                                    backgroundColor: item.checkNo === selectedCheckNo ? '#FFFF66' : '',
                                                }}
                                                onClick={() => item.void !== 'Y' && setSelectedCheckNo(item.checkNo)}
                                                onDoubleClick={() => item.void !== 'Y' && handleOpenDocuments(item)}
                                                title={item.void === 'Y' ? 'Voided' : 'Click to select (for Void) - double-click to manage documents'}
                                            >
                                                <td style={{ padding: 0 }}>
                                                    <RHFCheckbox
                                                        name={`checkRegisterItems[${index}].selection`}
                                                        register={register}
                                                        disabled={item.void === 'Y'}
                                                        bodyStyle={{ width: 'auto', margin: 0, display: 'inline-block' }}
                                                    />
                                                </td>
                                                <td>{item.periodNo}</td>
                                                <td>{item.tsdNo}</td>
                                                <td style={{ textAlign: 'center' }}>{item.office}</td>
                                                <td style={{ textAlign: 'left' }}>{item.paidTo}</td>
                                                <td>{item.checkNo}</td>
                                                <td>{formatDateForDisplay(item.checkDate)}</td>
                                                <td>{item.type}</td>
                                                <td style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                                                <td>{displayStatus(item)}</td>
                                                <td>{item.void}</td>
                                                <td>{displayDisbursement(item)}</td>
                                                <td>{displayCheckDoc(item)}</td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        reactIcon={FaBan}
                                                        iconStyle={{ fill: 'red', width: '2rem', height: '2rem', verticalAlign: 'center' }}
                                                        iconHoverColor="blue"
                                                        title="Void Check"
                                                        disabled={item.void === 'Y'}
                                                        style={{ height: '3rem', background: 'transparent' }}
                                                        onClick={() => handleVoidButtonClicked(item)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        {!loading && checkRegisterItems.length === 0 && (
                                            <tr>
                                                <td colSpan={14} className="bold-centered">No Checks Found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <CheckRegisterGenerateDialog
                dialogName={DialogList.CHECK_REGISTER_GENERATE}
                hideDialog={hideDialog}
                onGenerate={handleGenerateSubmit}
                message={generateMessage}
                nextCheckNo={nextCheckNo}
            />
            <CheckRegisterWithholdingDialog dialogName={DialogList.CHECK_REGISTER_WITHHOLDING} hideDialog={hideDialog} />
            <CheckRegisterWorkersCompDialog dialogName={DialogList.CHECK_REGISTER_WORKERS_COMP} hideDialog={hideDialog} />
            <CheckRegisterReportDialog
                dialogName={DialogList.CHECK_REGISTER_REPORT}
                hideDialog={hideDialog}
                onGenerateReport={handleGenerateReport}
                message={reportMessage}
            />
            <CheckDocumentManagementDialog
                dialogName={DialogList.CHECK_REGISTER_DOCUMENTS}
                hideDialog={hideDialog}
                checkNo={docsCheck?.checkNo}
                paidTo={docsCheck?.paidTo}
                periodNo={docsCheck?.periodNo}
            />
            <VoidCheckDialog
                dialogName={DialogList.CHECK_REGISTER_VOID}
                hideDialog={hideDialog}
                check={checkRegisterItems.find((i) => i.checkNo === selectedCheckNo)}
                onConfirm={handleConfirmVoid}
            />
            <CheckRegisterBNoticeDialog dialogName={DialogList.CHECK_REGISTER_BNOTICE} hideDialog={hideDialog} />
            <CheckRegisterStatsDialog dialogName={DialogList.CHECK_REGISTER_STATS} hideDialog={hideDialog} />
            <CheckRegisterExcelDialog dialogName={DialogList.CHECK_EXCEL_REPORT} hideDialog={hideDialog} excelType={excelType} />
            <CheckRegisterDownloadStubsMissingDialog
                dialogName={DialogList.CHECK_REGISTER_DOWNLOAD_STUBS_MISSING}
                hideDialog={hideDialog}
                heading="Stubs Not Saved"
                label={downloadStubsMissingLabel}
                canProceed={!downloadStubsMissingAllSelected}
                onProceed={handleProceedWithPartialStubDownload}
            />
            <CheckRegisterPrintTSDialog
                dialogName={DialogList.CHECK_REGISTER_PRINT_TS}
                hideDialog={hideDialog}
                onPrintTS={handlePrintTSSubmit}
                message={printTSMessage}
            />
            <NachaDialogs showDialog={showDialog} hideDialog={hideDialog} />
            <NoSelectedInvoicesWarningDialog
                dialogName={DialogList.CHECK_REGISTER_NO_SELECTION}
                hideDialog={hideDialog}
                heading="No Checks Selected"
                label={noSelectionMessage}
            />
        </div>
    );
};

export default CheckRegisterPage;



// WEBPACK FOOTER //
// ./src/components/personnel/pages/CheckRegisterPage.tsx